import Constants from 'expo-constants';
import nacl from 'tweetnacl';
import naclUtil from 'tweetnacl-util';

import { supabase } from '@/services/supabase';
import { getRuntimeClientBuild } from '@/services/runtime/clientBuild';
import { extractRecoveryHttpStatus } from '@/services/recovery/recoverySafeErrors';
import type {
  PreparedRecoveryEscrow,
  RecoveryCaptureStore,
  RecoveryEscrowObjectDescriptor,
  RecoveryEscrowTransport,
  RecoveryIdentity,
  RecoveryRemoteObjectInfo,
  RecoveryRemoteValidationGateway,
  RemoteRecoveryReceipt,
} from '@/types/recovery';

export const RECOVERY_ESCROW_BUCKET = 'app_guincho_recovery_escrow';

const sha512 = (bytes: Uint8Array): string => naclUtil.encodeBase64(nacl.hash(bytes));

const splitPath = (objectPath: string): { directory: string; fileName: string } => {
  if (!objectPath || objectPath.includes('..') || objectPath.startsWith('/')) {
    throw new Error('Caminho de escrow invalido.');
  }
  const separator = objectPath.lastIndexOf('/');
  if (separator <= 0 || separator === objectPath.length - 1) {
    throw new Error('Caminho de escrow incompleto.');
  }
  return { directory: objectPath.slice(0, separator), fileName: objectPath.slice(separator + 1) };
};

export class SupabaseRecoveryEscrowTransport implements RecoveryEscrowTransport {
  constructor(private readonly captureStore: Pick<RecoveryCaptureStore, 'readPersistedObject'>) {}

  async inspectObject(objectPath: string): Promise<RecoveryRemoteObjectInfo | null> {
    const { directory, fileName } = splitPath(objectPath);
    const { data, error } = await supabase.storage
      .from(RECOVERY_ESCROW_BUCKET)
      .list(directory, {
        limit: 1,
        search: fileName,
      });
    if (error) {
      const status = extractRecoveryHttpStatus(error);
      if (status === 400 || status === 404) return null;
      throw error;
    }
    const found = data?.find(item => item.name === fileName);
    if (!found) return null;
    const metadata = (found as any).metadata ?? {};
    const size = Number(
      (found as any).size ??
      metadata.size ??
      metadata.contentLength
    );
    if (!Number.isFinite(size) || size <= 0) return null;
    return {
      objectPath,
      ciphertextBytes: size,
      ciphertextSha512: '',
    };
  }

  async uploadImmutable(object: RecoveryEscrowObjectDescriptor): Promise<void> {
    const bytes = await this.captureStore.readPersistedObject(object.localUri);
    if (
      !bytes ||
      bytes.byteLength !== object.ciphertextBytes ||
      sha512(bytes) !== object.ciphertextSha512
    ) {
      throw new Error('Objeto local do escrow ausente ou corrompido.');
    }
    const { error } = await supabase.storage.from(RECOVERY_ESCROW_BUCKET).upload(
      object.objectPath,
      bytes,
      {
        contentType: object.contentType,
        upsert: false,
        metadata: {
          sha512: object.ciphertextSha512,
          bytes: object.ciphertextBytes,
        },
      }
    );
    if (error) throw error;
  }
}

const currentBuild = (): number => {
  const value = getRuntimeClientBuild();
  if (value <= 0) throw new Error('Build canario invalido.');
  return value;
};

const currentVersion = (): string => Constants.expoConfig?.version ?? 'unknown';

const normalizeReceipt = (value: unknown): RemoteRecoveryReceipt | null => {
  if (!value || typeof value !== 'object') return null;
  const raw = value as Record<string, any>;
  if (!raw.receipt_id && !raw.receiptId) return null;
  const payload = raw.attestation_payload ?? raw.attestationPayload;
  return {
    schema: 'app-guincho-recovery-receipt/v1',
    receiptId: String(raw.receipt_id ?? raw.receiptId),
    recoveryId: String(raw.recovery_id ?? raw.recoveryId),
    companyId: String(raw.company_id ?? raw.companyId),
    userId: String(raw.user_id ?? raw.userId),
    deviceId: String(raw.device_id ?? raw.deviceId),
    manifestSha512: String(raw.manifest_sha512 ?? raw.manifestSha512),
    status: 'validated',
    validatedAt: String(raw.validated_at ?? raw.validatedAt),
    expiresAt: raw.expires_at ?? raw.expiresAt ?? null,
    authority: String(raw.authority ?? raw.validator_key_id ?? ''),
    proof: String(raw.proof ?? raw.attestation_signature ?? ''),
    attestationPayload: payload,
  } as RemoteRecoveryReceipt;
};

export class SupabaseRecoveryValidationGateway implements RecoveryRemoteValidationGateway {
  async registerUpload(input: {
    identity: RecoveryIdentity;
    recoveryId: string;
    prepared: PreparedRecoveryEscrow;
    idempotencyKey: string;
  }): Promise<void> {
    const { prepared } = input;
    const { data: beginData, error: beginError } = await supabase.rpc('begin_recovery_escrow_v1', {
      p_client_request_id: input.recoveryId,
      p_device_id: input.identity.deviceId,
      p_client_build: currentBuild(),
      p_manifest: {
        app_version: currentVersion(),
        pending_items_count: prepared.manifest.pendingItemCount,
        manifest_object_path: prepared.manifestObject.objectPath,
        manifest_sha512: prepared.manifestObject.ciphertextSha512,
        table_counts: prepared.manifest.tableCounts,
        idempotency_key: input.idempotencyKey,
      },
      p_expected_parts: prepared.manifest.partCount,
      p_expected_bytes: prepared.manifest.totalCiphertextBytes,
      p_logical_sha512: prepared.manifest.logicalSha512,
    });
    if (beginError) throw beginError;
    const first = Array.isArray(beginData) ? beginData[0] : beginData;
    const remoteRecoveryId = String(first?.recovery_id ?? first?.recoveryId ?? '');
    if (remoteRecoveryId !== input.recoveryId) {
      throw new Error('Recovery remoto diverge do pacote local.');
    }
  }

  async requestValidation(input: {
    identity: RecoveryIdentity;
    recoveryId: string;
    prepared: PreparedRecoveryEscrow;
    idempotencyKey: string;
  }): Promise<void> {
    const { prepared } = input;
    const { error: completeError } = await supabase.rpc('complete_recovery_upload_v1', {
      p_recovery_id: input.recoveryId,
      p_objects: prepared.manifest.parts.map(part => ({
        part_number: part.index,
        storage_path: part.objectPath,
        cipher_sha512: part.ciphertextSha512,
        size_bytes: part.ciphertextBytes,
        storage_etag: null,
      })),
    });
    if (completeError) throw completeError;

    const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
    if (sessionError) throw sessionError;
    const accessToken = sessionData.session?.access_token;
    if (!accessToken) {
      throw new Error('Sessao autenticada indisponivel para validacao automatica.');
    }

    const { error: validationError } = await supabase.functions.invoke(
      'auto-validate-recovery-escrow',
      {
        body: { recoveryId: input.recoveryId },
        headers: { Authorization: `Bearer ${accessToken}` },
      }
    );
    if (validationError) throw validationError;
  }

  async fetchReceipt(input: {
    identity: RecoveryIdentity;
    recoveryId: string;
  }): Promise<RemoteRecoveryReceipt | null> {
    const { data, error } = await supabase.rpc('get_recovery_receipt_v1', {
      p_recovery_id: input.recoveryId,
    });
    if (error) throw error;
    const receipt = normalizeReceipt(data);
    if (!receipt) return null;
    if (
      receipt.companyId !== input.identity.companyId ||
      receipt.userId !== input.identity.userId ||
      receipt.deviceId !== input.identity.deviceId ||
      receipt.recoveryId !== input.recoveryId
    ) {
      throw new Error('Receipt remoto pertence a outra identidade.');
    }
    return receipt;
  }
}
